import { addPence, pence } from '@cf/shared';
import { assertReviewed } from './review.js';
import { excessOver, mulBps } from './bps.js';
/**
 * Inheritance tax — an illustrative estate figure (clearly labelled, never
 * advice): nil-rate band, residence nil-rate band (with the £2m taper),
 * transferred allowances from a pre-deceased spouse, full spouse exemption,
 * the standard/charity rate, PLUS failed potentially-exempt transfers (PETs)
 * with the 7-year clock and taper relief, the annual gift exemption, and
 * business property relief. Trusts remain out of scope (Phase 1 boundary).
 *
 * PET mechanics (HMRC): gifts within 7 years of death are added back; the
 * nil-rate band is set against the EARLIEST gifts first, and any tax on the
 * remainder is reduced by taper relief on a sliding scale by years survived.
 * Business property relief removes the qualifying value before the gift bites.
 */
/**
 * A2.2. Spelled once: the adviser's asserted position that a gift is normal
 * expenditure out of income (IHTA 1984 s.21), which takes it out of the
 * seven-year clock altogether.
 */
const NORMAL_EXPENDITURE = 'normalExpenditureOutOfIncome';
/** A transfer percentage in basis points: a whole number from 0 to 10,000. */
function transferBps(label, full, stated) {
    if (stated !== undefined && (!Number.isInteger(stated) || stated < 0 || stated > 10_000)) {
        throw new RangeError(`${label} must be a whole number of basis points from 0 to 10000`);
    }
    // s.8A(5) / s.8G: the transferred part never exceeds 100% of the band.
    return full ? 10_000 : (stated ?? 0);
}
/** Taper multiplier (bps of the full charge) for a gift by years survived. */
function taperRateBps(rules, yearsBeforeDeath) {
    const bands = rules.inheritanceTax.giftTaperBands;
    if (bands === undefined)
        return 10_000;
    for (const b of bands) {
        if (yearsBeforeDeath >= b.fromYears && yearsBeforeDeath < b.toYears)
            return b.rateBps;
    }
    return 10_000; // within 3 years (or no matching band): full charge.
}
export function inheritanceTax(rules, input) {
    assertReviewed(rules);
    const r = rules.inheritanceTax;
    if (!Number.isSafeInteger(input.estateValue) || input.estateValue < 0) {
        throw new RangeError('Estate value must be a non-negative integer of pence');
    }
    if (input.priorYearExemptionUsed !== undefined &&
        (!Number.isSafeInteger(input.priorYearExemptionUsed) || input.priorYearExemptionUsed < 0)) {
        throw new RangeError('Annual exemption used in the prior tax year must be a non-negative integer of pence');
    }
    if (input.estateValueForTaper !== undefined &&
        (!Number.isSafeInteger(input.estateValueForTaper) || input.estateValueForTaper < 0)) {
        throw new RangeError('Estate value for the taper must be a non-negative integer of pence');
    }
    // A2.8 review (tech-admin): this calculator is exported from `@cf/tax` and can
    // be called without the contract in front of it. A value the walk does not
    // recognise used to fall through to the `'annual'` branch, so a typo — or a
    // value from a LATER contract, such as A2.2's `normalExpenditureOutOfIncome`
    // — silently GRANTED the exemption and understated the tax. Failing open on
    // an unrecognised tax input is the wrong direction, so it now throws.
    for (const g of input.lifetimeGifts ?? []) {
        if (g.exemptionClaimed !== undefined &&
            g.exemptionClaimed !== 'annual' &&
            g.exemptionClaimed !== 'none' &&
            g.exemptionClaimed !== NORMAL_EXPENDITURE) {
            throw new RangeError(`Unrecognised exemptionClaimed on a lifetime gift: ${String(g.exemptionClaimed)}`);
        }
    }
    const nrbTransferBps = transferBps('Transferred nil-rate band', input.hasFullTransferredAllowances, input.transferredNilRateBandBps);
    const rnrbTransferBps = transferBps('Transferred residence nil-rate band', input.hasFullTransferredAllowances, input.transferredResidenceNilRateBandBps);
    // Truncated, like every bps product here: an allowance rounded down never
    // understates the tax. At 10,000 bps it is the whole band, exactly.
    const nrbTransferred = mulBps(r.nilRateBand, nrbTransferBps);
    let nrbRemaining = r.nilRateBand + nrbTransferred;
    const nrbTotal = pence(nrbRemaining);
    const estateRate = input.charityRateApplies ? r.charityRateBps : r.standardRateBps;
    // 1. Failed PETs: earliest gifts consume the NRB first; taper reduces the
    //    tax on any remainder. BPR and the annual exemption reduce each gift.
    const annualExemption = r.annualGiftExemption ?? pence(0);
    /**
     * A2.1. The annual exemption is £3,000 PER TAX YEAR, and unused exemption
     * carries forward to the NEXT tax year only, the current year's own being
     * used first (GOV.UK "How Inheritance Tax on a gift is worked out";
     * IHTM14141-14143). This was a single pool drained across a whole lifetime of
     * gifts, so a client gifting £3,000 a year for ten years was given £3,000 of
     * exemption where HMRC gives £30,000 — the commonest intergenerational plan
     * an adviser models, and the estate estimate came out roughly £7,200 too
     * high. Evidence: docs/tax/iht-gifts-exemptions.md.
     *
     * `yearsBeforeDeath` is the tax-year key: gifts sharing it were made in the
     * same tax year. Gifts OUTSIDE the seven-year window are not taxed but DO
     * spend their own year's exemption, so they are walked here and excluded
     * from the charge below — ignoring them would overstate what carries forward.
     */
    const allGifts = (input.lifetimeGifts ?? [])
        .map((g, inputIndex) => ({ g, inputIndex }))
        .filter(({ g }) => g.value > 0)
        .sort((a, b) => b.g.yearsBeforeDeath - a.g.yearsBeforeDeath); // earliest first
    /**
     * A2.1. The marriage exemption is taken BEFORE the annual one and is
     * additional to it — GOV.UK's own worked case is a £5,000 wedding gift to a
     * child plus £3,000 of annual exemption in the same tax year. Applying the
     * annual exemption first would burn it on a gift the wedding limit already
     * covered, and leave less for every other gift that year.
     */
    const weddingLimits = r.weddingGiftExemption;
    /**
     * Remaining marriage exemption per WEDDING, keyed on the relationship and who
     * is marrying. The limit is per donor per marriage, so several gifts to one
     * wedding share it — including across tax years, which is why this pool sits
     * outside the per-year walk. An unnamed wedding shares one pool per
     * relationship: the tool cannot distinguish two marriages, and sharing
     * overstates the tax rather than understating it.
     */
    const weddingPool = new Map();
    const weddingExemptionFor = (g, afterBpr) => {
        // A2.2: one basis or the other. A gift already wholly exempt as normal
        // expenditure must not also draw down a marriage limit that another gift to
        // the same wedding could use.
        if (g.exemptionClaimed === NORMAL_EXPENDITURE)
            return 0;
        if (g.weddingGiftFrom === undefined || weddingLimits === undefined)
            return 0;
        const key = `${g.weddingGiftFrom}:${g.weddingFor ?? ''}`;
        const left = weddingPool.get(key) ?? weddingLimits[g.weddingGiftFrom];
        const used = Math.min(left, afterBpr);
        weddingPool.set(key, left - used);
        return used;
    };
    const weddingByIndex = new Map();
    const exemptionByIndex = new Map();
    if (allGifts.length > 0) {
        // EVERY tax year is walked, not only the ones with a gift: a year with no
        // recorded gift leaves its whole allowance unused, and that unused amount
        // is exactly what carries into the next year. Starting one year above the
        // earliest recorded gift is the single assumption here — the plan's gift
        // list is the adviser's complete record, so a year holding no gift did not
        // have one. Stated in docs/tax/iht-gifts-exemptions.md.
        /**
         * A2.2 review (tech-admin). Derived from the gifts that can actually SPEND
         * the allowance, not from every gift in the ledger.
         *
         * A gift asserted as normal expenditure spends nothing here — but it was
         * still moving the year the walk starts from, and the walk treats every
         * year above the earliest recorded gift as fully unused. So recording the
         * history of a long-running s.21 arrangement back to 2019 — which is the
         * INTENDED use, a habitual pattern being exactly what s.21 requires —
         * manufactured years of unused allowance and carried it down to an
         * unrelated chargeable gift. A two-gift probe doubled that gift's exemption
         * from £3,000 to £6,000 and understated the tax by £1,200, and it silently
         * relocated a stated `priorYearExemptionUsed` to a year that then expired.
         *
         * Falls back to the full set only if every gift is asserted, where the walk
         * has nothing to allocate anyway.
         */
        const spending = allGifts.filter(({ g }) => g.exemptionClaimed !== NORMAL_EXPENDITURE);
        const walked = spending.length > 0 ? spending : allGifts;
        const firstYear = Math.max(...walked.map(({ g }) => g.yearsBeforeDeath)) + 1;
        // Plain-number accumulators: these are working arithmetic within one
        // exemption walk, and every value that leaves it is re-branded through
        // `pence()` at the point it is written out.
        let carriedIn = 0;
        for (let year = firstYear; year >= 0; year--) {
            /**
             * A2.8. `firstYear` is the one year the walk cannot see: it sits above
             * every recorded gift, so nothing in the plan says what it was used for.
             * Where the adviser has stated it, that statement stands in place of the
             * assumption; where they have not, the year is still taken as unused and
             * the engine says so (`gift-exemption-pre-plan-unused`). Clamped so an
             * over-stated figure cannot make an allowance negative.
             */
            const priorUsed = year === firstYear ? Math.min(annualExemption, input.priorYearExemptionUsed ?? 0) : 0;
            let own = annualExemption - priorUsed;
            let carried = carriedIn;
            for (const { g, inputIndex } of allGifts) {
                if (g.yearsBeforeDeath !== year)
                    continue;
                const afterBpr = g.value - mulBps(pence(g.value), g.bprRateBps ?? 0);
                const wedding = weddingExemptionFor(g, afterBpr);
                weddingByIndex.set(inputIndex, wedding);
                const afterWedding = afterBpr - wedding;
                // A2.8: a gift the adviser has stated claims no annual exemption draws
                // none AND spends none — the year's allowance is left intact for the
                // gift that needs it, which is the whole reason the field exists.
                // A2.2: a gift asserted as normal expenditure out of income is exempt
                // on a different basis, so it likewise spends nothing here.
                if (g.exemptionClaimed === 'none' || g.exemptionClaimed === NORMAL_EXPENDITURE) {
                    exemptionByIndex.set(inputIndex, 0);
                    continue;
                }
                // The current year's own exemption is spent before anything carried in.
                const fromOwn = Math.min(own, afterWedding);
                own -= fromOwn;
                const fromCarried = Math.min(carried, afterWedding - fromOwn);
                carried -= fromCarried;
                exemptionByIndex.set(inputIndex, fromOwn + fromCarried);
            }
            // Only the year's OWN unused allowance travels; anything carried into
            // this year and still unspent expires here.
            carriedIn = own;
        }
    }
    /**
     * A2.1 review (client-reviewer). A detail is emitted for EVERY gift walked,
     * not only the ones inside the charge window. A gift at exactly seven years
     * is not taxed, but it DID spend its own tax year's exemption — and printing
     * "Exemption applied: £0" against it, directly above an identical gift a year
     * later showing £3,000, reads to a client as an arithmetic error in their own
     * report. Such a gift consumes no nil-rate band and bears no tax.
     */
    const gifts = allGifts;
    let taxOnGifts = 0;
    let chargeableGifts = 0;
    const giftDetails = [];
    for (const { g, inputIndex } of gifts) {
        const bprRelief = mulBps(pence(g.value), g.bprRateBps ?? 0);
        const afterBpr = g.value - bprRelief;
        const weddingUsed = weddingByIndex.get(inputIndex) ?? 0;
        const exemptUsed = exemptionByIndex.get(inputIndex) ?? 0;
        const netGift = afterBpr - weddingUsed - exemptUsed;
        /**
         * A2.2. Asserted as normal expenditure out of income, so under s.21 this is
         * not a potentially exempt transfer at all: it never enters the window, and
         * every consequence of being in it — the nil-rate band it would consume, the
         * charge, the taper that would relieve that charge — is simply absent. The
         * gift is still REPORTED, because money that left the plan with no row
         * explaining where it went is worse than a row saying it was exempt.
         */
        const assertedNormalExpenditure = g.exemptionClaimed === NORMAL_EXPENDITURE;
        const inWindow = !assertedNormalExpenditure && g.yearsBeforeDeath < 7;
        // Outside the window the gift is out of account entirely: no band, no tax.
        const coveredByNrb = inWindow ? Math.min(nrbRemaining, netGift) : 0;
        nrbRemaining -= coveredByNrb;
        const taxable = inWindow ? netGift - coveredByNrb : 0;
        // Taper is relief on a charge; with no charge there is nothing to relieve,
        // and printing "20% of the tax on this gift" beside a £0 that arises for a
        // different reason would misdescribe it.
        const taperBps = assertedNormalExpenditure ? 0 : taperRateBps(rules, g.yearsBeforeDeath);
        let tax = 0;
        if (taxable > 0) {
            chargeableGifts += taxable;
            const fullTax = mulBps(pence(taxable), estateRate);
            tax = mulBps(fullTax, taperBps);
            taxOnGifts += tax;
        }
        giftDetails.push({
            inputIndex,
            value: g.value,
            yearsBeforeDeath: g.yearsBeforeDeath,
            bprRelief,
            exemptionUsed: pence(exemptUsed),
            weddingExemption: pence(weddingUsed),
            coveredByNrb: pence(coveredByNrb),
            taxable: pence(taxable),
            taperBps,
            tax: pence(tax),
        });
    }
    // 2. Residence nil-rate band: the maximum (own + transferred) is tapered £1
    //    per £2 of the estate above the threshold, and what is left is limited
    //    to the residence value (IHTA 1984 s.8D–8E). RNRB never meets gifts.
    //    IE-1: the taper is tested on the estate BEFORE exemptions and reliefs.
    const estateForTaper = input.estateValueForTaper ?? input.estateValue;
    const rnrbTransferred = mulBps(r.residenceNilRateBand, rnrbTransferBps);
    const rnrbMaximum = r.residenceNilRateBand + rnrbTransferred;
    const rnrbTaper = Math.min(rnrbMaximum, Math.trunc(excessOver(estateForTaper, r.residenceNilRateBandTaperThreshold) / 2));
    const rnrbAfterTaper = rnrbMaximum - rnrbTaper;
    const residenceValue = Math.max(0, input.residenceToDescendants);
    const rnrb = Math.min(rnrbAfterTaper, residenceValue);
    // 3. Estate: the spouse exemption takes it out of charge entirely; otherwise
    //    the residence band is set against it first (it can shelter nothing
    //    else), then what is left of the nil-rate band, and the rest is charged.
    const estateExempt = input.passesToSpouse ? input.estateValue : 0;
    const estateBeforeBands = input.estateValue - estateExempt;
    const rnrbUsed = Math.min(rnrb, estateBeforeBands);
    const nrbUsedByEstate = Math.min(nrbRemaining, estateBeforeBands - rnrbUsed);
    const chargeableEstate = pence(estateBeforeBands - rnrbUsed - nrbUsedByEstate);
    const taxOnEstate = mulBps(chargeableEstate, estateRate);
    const breakdown = {
        estateValueForTaper: pence(estateForTaper),
        estateExempt: pence(estateExempt),
        estateBeforeBands: pence(estateBeforeBands),
        nilRateBand: {
            own: r.nilRateBand,
            transferredBps: nrbTransferBps,
            transferred: nrbTransferred,
            total: nrbTotal,
            usedByGifts: pence(nrbTotal - nrbRemaining),
            remainingAfterGifts: pence(nrbRemaining),
            usedByEstate: pence(nrbUsedByEstate),
        },
        residenceNilRateBand: {
            own: r.residenceNilRateBand,
            transferredBps: rnrbTransferBps,
            transferred: rnrbTransferred,
            maximum: pence(rnrbMaximum),
            taperReduction: pence(rnrbTaper),
            afterTaper: pence(rnrbAfterTaper),
            residenceValue: pence(residenceValue),
            available: pence(rnrb),
            usedByEstate: pence(rnrbUsed),
        },
        taxableEstate: chargeableEstate,
        rateBps: estateRate,
    };
    return {
        nilRateBandTotal: nrbTotal,
        residenceNilRateBandTotal: pence(rnrb),
        chargeableGifts: pence(chargeableGifts),
        chargeableEstate,
        taxOnGifts: pence(taxOnGifts),
        taxOnEstate,
        taxDue: addPence(pence(taxOnGifts), taxOnEstate),
        giftDetails,
        breakdown,
    };
}
//# sourceMappingURL=inheritance-tax.js.map